/**
 * Validates and checks if the provided URL is a valid Google Fonts URL.
 *
 * Steps:
 * - Matches the input string against a basic URL pattern.
 * - Uses JavaScript's URL API to parse the URL.
 * - Ensures the URL belongs to `fonts.googleapis.com` and contains necessary properties (e.g., "family" parameter).
 * - Returns the cleaned and valid URL or `false` if invalid.
 *
 * Note:
 * - Check the official Google Fonts documentation periodically to ensure the URL format or domain has not changed:
 *   https://fonts.google.com/
 *
 * @param url - The string input to validate as a Google Fonts URL.
 * @returns {string | false} Returns a valid URL as a string or `false` if it fails the checks.
 */
export const getValidGoogleFontsUrl = (url: string) => {
  try {
    const urlRegex = /https?:\/\/[^\s"'`<>]+/i;
    const urlMatch = url.trim().match(urlRegex);

    if (!urlMatch) {
      return false;
    }

    const urlToCheck = urlMatch[0].trim().replace(/^['"`;]+|['"`;]+$/g, '');
    const { hostname, pathname, searchParams } = new URL(urlToCheck);
    const isGoogleHost = hostname === 'fonts.googleapis.com';
    const isCssPath = /^\/css2?/.test(pathname);
    const hasFamilyParam = searchParams.has('family');

    return isGoogleHost && isCssPath && hasFamilyParam ? urlToCheck : false;
  } catch (error) {
    console.warn(`Error parsing URL: ${url}`, error);
    return false;
  }
};
